import { useState } from "react";
import { Loader2, Play, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { playTick, playFanfare, confetti } from "@/lib/celebrate";
import { useSkill, SkillInfo, SkillTop, SkillResult } from "./SkillShell";

const IMAGES = [
  "https://images.unsplash.com/photo-1514923995763-768e52f5af87?w=600&q=80",
  "https://images.unsplash.com/photo-1702745572427-c7c772b45ff3?w=600&q=80",
  "https://images.unsplash.com/photo-1517520267752-34bfde704a0f?w=600&q=80",
  "https://images.unsplash.com/photo-1497531551184-06b252e1bee1?w=600&q=80",
];
const SOLVED = [1, 2, 3, 4, 5, 6, 7, 8, 0];
const RULES = ["Jeden Tag gibt es ein neues Puzzle – für alle gleich.", "Tippe ein Teil neben der Lücke an, um es zu verschieben.", "Bring die Teile in die Reihenfolge 1–8 (die Lücke unten rechts). Weniger Züge = mehr Punkte."];

const adjacent = (a, b) => (Math.abs(a - b) === 3) || (Math.abs(a - b) === 1 && Math.floor(a / 3) === Math.floor(b / 3));

export function PuzzleGame() {
  const { ov, load } = useSkill();
  const [s, setS] = useState(null);
  const [result, setResult] = useState(null);
  const [sending, setSending] = useState(false);
  const start = async () => {
    try {
      const { data } = await api.post("/skill/puzzle/start");
      setResult(null); setS({ ...data, initial: data.board, moves: [] });
    } catch (e) { toast.error(formatErr(e)); }
  };
  const move = async (pos) => {
    if (!s || sending) return;
    const blank = s.board.indexOf(0), tile = s.board[pos];
    if (!tile || !adjacent(pos, blank)) return;
    const board = [...s.board]; board[blank] = tile; board[pos] = 0;
    const moves = [...s.moves, tile];
    playTick();
    setS((p) => ({ ...p, board, moves }));
    if (board.join() !== SOLVED.join()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/skill/puzzle/${s.id}/finish`, { moves });
      playFanfare(); confetti({ count: 90 }); setResult(data); load();
    } catch (e) { toast.error(formatErr(e)); } finally { setSending(false); }
  };
  if (!ov) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-rose-500" /></div>;
  const g = ov.games.puzzle;
  const img = IMAGES[s?.image ?? 0];
  return (
    <div className="space-y-4" data-testid="puzzle-game">
      <SkillInfo game="puzzle" g={g} rules={RULES} />
      {result ? <SkillResult game="puzzle" result={result} onAgain={start} /> : !s ? (
        <button type="button" onClick={start} data-testid="puzzle-start" className="tap w-full h-14 rounded-full pink-gradient text-white font-extrabold text-lg pink-glow inline-flex items-center justify-center gap-2"><Play className="w-5 h-5" fill="currentColor" /> Tages-Puzzle starten</button>
      ) : (
        <div className="lux-card rounded-3xl p-4 space-y-3">
          <div className="flex items-center justify-between text-sm font-bold text-slate-600">
            <span data-testid="puzzle-moves">Züge: <span className="tabular-nums text-rose-600">{s.moves.length}</span></span>
            <button type="button" onClick={() => setS((p) => ({ ...p, board: p.initial, moves: [] }))} data-testid="puzzle-reset" className="tap inline-flex items-center gap-1 px-3 h-8 rounded-full bg-rose-50 text-rose-600 text-xs font-extrabold"><RotateCcw className="w-3.5 h-3.5" /> Zurücksetzen</button>
          </div>
          <div className="grid grid-cols-3 gap-1.5 p-1.5 rounded-2xl bg-rose-100/70" data-testid="puzzle-board">
            {s.board.map((t, pos) => t === 0 ? <span key="blank" className="aspect-square rounded-xl bg-white/50" data-testid="puzzle-blank" /> : (
              <button key={t} type="button" onClick={() => move(pos)} data-testid={`puzzle-tile-${t}`}
                className="tile-move relative aspect-square rounded-xl overflow-hidden shadow-md active:scale-95 border border-white/60"
                style={{ backgroundImage: `url(${img})`, backgroundSize: "300% 300%", backgroundPosition: `${((t - 1) % 3) * 50}% ${Math.floor((t - 1) / 3) * 50}%` }}>
                <span className="absolute top-1 left-1 w-6 h-6 rounded-full bg-white/90 text-rose-600 text-xs font-black flex items-center justify-center shadow">{t}</span>
              </button>
            ))}
          </div>
          {sending && <p className="text-center text-sm font-bold text-rose-600"><Loader2 className="inline w-4 h-4 animate-spin" /> Wird gewertet …</p>}
        </div>
      )}
      <SkillTop game="puzzle" g={g} />
    </div>
  );
}
